import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface BroadcastDto {
  id: string;
  organisation_id: string;
  sender_id: string;
  message: string;
  recipient_count: number;
  delivered_count: number;
  failed_count: number;
  sent_at: string;
}

export type BroadcastRecipientStatus = 'queued' | 'sent' | 'delivered' | 'failed';

export interface BroadcastRecipientDto {
  id: string;
  broadcast_id: string;
  contact_id: string | null;
  user_id: string | null;
  phone_e164: string;
  status: BroadcastRecipientStatus;
  provider_msg_id: string | null;
  error_message: string | null;
}

export interface SendBroadcastInput {
  message: string;
  team_ids?: string[];
  user_ids?: string[];
  contact_ids?: string[];
  send_to_all_contacts?: boolean;
}

export function useBroadcastsList() {
  const api = useApi();
  return useQuery({
    queryKey: ['broadcasts', 'list'],
    queryFn: () => api<{ broadcasts: BroadcastDto[] }>('/broadcasts'),
    refetchInterval: 30_000,
  });
}

export function useBroadcast(id: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['broadcasts', id],
    queryFn: () =>
      api<{ broadcast: BroadcastDto; recipients: BroadcastRecipientDto[] }>(`/broadcasts/${id}`),
    enabled: Boolean(id),
    refetchInterval: 15_000,
  });
}

export function useSendBroadcast() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SendBroadcastInput) =>
      api<{ broadcast: BroadcastDto; recipients: BroadcastRecipientDto[] }>('/broadcasts', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['broadcasts'] }),
  });
}
